import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Organization } from '../models/organization.model';
import { BaseResponse, BaseResponseObj } from 'ccc-commons/dist/core';
import { Setting } from '../models/setting.model';
import {OrganizationFiltersModel} from '@ccc-core/models/organization-filters.model';
import {CoachingOrganization} from '@ccc-core/models/coaching-organization.model';

@Injectable()
export class OrganizationService {
  apiOrganizationsUrl = environment.apiUrl + '/organizations';
  apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  /**
   * getOrganizations
   * get the list of organizations
   * @param params filters for the organizations
   * @returns `Observable` with the organizations list
   */
  public getOrganizations(params?: OrganizationFiltersModel): Observable<BaseResponse<Organization>> {
    const httpParams = new HttpParams({ fromObject: Object(params) });
    return this.http.get<BaseResponse<Organization>>(`${this.apiOrganizationsUrl}`,
      { params: httpParams });
  }

  getOrganizationById(orgId: any): Observable<BaseResponseObj<Organization>> {
    return this.http.get<BaseResponseObj<Organization>>(`${this.apiOrganizationsUrl}/${orgId}`);
  }

  getOrganizationSuggestions(params?: any): Observable<BaseResponse<Organization>> {
    const httpParams = new HttpParams({ fromObject: params });
    return this.http.get<BaseResponse<Organization>>(`${this.apiUrl}/suggestions/organizations`,
      {
        params: httpParams
      });
  }

  getOrganizationTree(orgId: string): Observable<BaseResponseObj<any[]>> {
    return this.http.get<BaseResponseObj<any[]>>(`${this.apiUrl}/organizations/${orgId}/tree`);
  }

  addOrganization(organization): Observable<BaseResponseObj<Organization>> {
    return this.http.post<BaseResponseObj<Organization>>(
      `${this.apiOrganizationsUrl}`,
      organization
    );
  }

  updateOrganization(organization, orgId): Observable<BaseResponseObj<Organization>> {
    return this.http.put<BaseResponseObj<Organization>>(
      `${this.apiOrganizationsUrl}/${orgId}`,
      organization
    );
  }

  updateOrganizationStatus(orgId, params): Observable<BaseResponseObj<Organization>> {
    // const httpParams = new HttpParams({ fromObject: params });

    return this.http.put<BaseResponseObj<Organization>>(
      `${this.apiOrganizationsUrl}/${orgId}/status`,
      params);
  }


  updateOrganizationAllowLocation(orgId: string, organization): Observable<BaseResponseObj<Organization>> {
    return this.http.patch<BaseResponseObj<Organization>>(`${this.apiOrganizationsUrl}/${orgId}`, organization);
  }

  getAffiliationsSorted(
    affiliations: Organization[]
  ): Organization[] {

    if (!affiliations) {
      return [];
    }
    let allSchools: Organization[] = [];

    for (const aff of affiliations) {
      allSchools.push(aff);
      allSchools = this.processAffChild(aff, allSchools);
    }
    return allSchools;
  }

  private processAffChild(aff, allSchools) {

    if (aff.children) {
      for (const child of aff.children) {
        let space = '';
        for (let i = 1; i < child.level; i++) {
          space += '&nbsp;';
        }
        child.name = child.name.replace(/&nbsp;/g, '');
        child.name = space + child.name;
        allSchools.push(child);

        if (child.children) {
          allSchools = this.processAffChild(child, allSchools);
        }
      }
    }

    return allSchools;
  }



  getSorted(affiliations, allSchools, level = 0, ) {
    if (!affiliations) {
      return [];
    }

    for (const aff of affiliations) {
      let space = '';
      for (let i = 1; i < aff.level; i++) {
        space += '&nbsp;';
      }
      aff.name = aff.name.replace(/&nbsp;/g, '');
      aff.name = space + aff.name;
      allSchools.push(aff);

      if (aff.children) {
        this.getSorted(aff.children, allSchools, level++);
      }
    }

    return allSchools;


  }

  removeSpaces(str) {
    const regEx = new RegExp(String.fromCharCode(160), 'g');
    return str.replace(regEx, '');
  }

  getOrganizationSettings(orgId): Observable<BaseResponseObj<Setting[]>> {
    return this.http.get<BaseResponseObj<Setting[]>>(`${this.apiOrganizationsUrl}/${orgId}/settings`);
  }

  setOrganizationSettings(settings: any, orgId): Observable<BaseResponseObj<Organization>> {
    return this.http.patch<BaseResponseObj<Organization>>(`${this.apiOrganizationsUrl}/${orgId}/settings`, settings);
  }

  getOrganizationRole(orgId: string): Observable<BaseResponseObj<any>> {
    return this.http.get<BaseResponseObj<any>>(`${this.apiOrganizationsUrl}/${orgId}/roles`);
  }

  getPrograms(orgId: string): Observable<BaseResponse<any>> {
    return this.http.get<BaseResponse<any>>(`${this.apiUrl}/organizations/${orgId}/programs`);
  }

  getAllowedDomains(orgID: string): Observable<BaseResponse<any>> {
    return this.http.get<BaseResponse<any>>(`${this.apiUrl}/organizations/${orgID}/domains`);
  }

  updateAllowedDomains(domains, orgId: string): Observable<BaseResponseObj<any>> {
    return this.http.put<BaseResponseObj<any>>(
      `${this.apiUrl}/organizations/${orgId}/domains`,
      domains
    );
  }

  /**
   * getCoachingOrganizations
   * get the list of organizations able to receive coaching
   * @param sort the field to sort by
   */
  public getCoachingOrganizations(sort: string): Observable<BaseResponseObj<CoachingOrganization[]>> {
    return this.http.get<BaseResponseObj<CoachingOrganization[]>>(`${this.apiUrl}/organizations/coaching?sort=${sort}`);
  }

  /**
   * saveDistrictCoaches
   * assigns a list of coach admins
   * @param districts array of district ids
   * @param coaches array of coach ids
   */
  public saveDistrictsCoaches(districts: string[], coaches: string[]): Observable<any> {
    const url = `${this.apiUrl}/accounts/coaches/district`;
    return this.http.post(url, {
      orgs: districts,
      coaches: coaches
    });
  }
}
